/**
 * @fileoverview Pinia store for Custom Mosaics.
 *
 * A design is one pattern from `data/patterns.json`, a grout color, and a map
 * of tile id → Original Ceramic glaze. Tiles start unpainted; the user picks a
 * glaze and paints tiles one at a time (or by fill / by size).
 *
 * Colors are kept as an immutable object that is replaced on every change, so
 * history snapshots are just references.
 *
 * @module mosaicStore
 */
import { defineStore } from 'pinia';
import { ref, reactive, computed, watch } from 'vue';
import { liteClient as algoliasearch } from 'algoliasearch/lite';
import { glazeColors as staticGlazeColors, patternsData, groutData } from '../data/index.js';
import { loadPatterns } from '../utils/patternData.js';
import { encodeDesign, decodeColors, buildShareUrl } from '../utils/designCodec.js';
import { saveDesign, loadDesign, loadLastPatternId } from '../utils/persistence.js';
import { buildPartsList } from '../utils/partsList.js';
import { derivePaletteFromRecords, isOfferedGlaze } from '../utils/glazeRecords.js';
import { log, error as logError } from '../utils/logger.js';

export const MIN_REPEAT = 1;
export const MAX_REPEAT = 6;
const MAX_HISTORY = 50;
const SAVE_DEBOUNCE_MS = 250;

const { patterns: PATTERNS, errors: PATTERN_ERRORS } = loadPatterns(patternsData);
if (PATTERN_ERRORS.length) {
    console.error('[CustomMosaics] patterns.json failed validation:', PATTERN_ERRORS);
}
const PATTERNS_BY_ID = new Map(PATTERNS.map((p) => [p.id, p]));
const GROUT_COLORS = groutData.colors || [];
const GROUT_BY_ID = new Map(GROUT_COLORS.map((g) => [g.id, g]));
const DEFAULT_GROUT_ID = GROUT_BY_ID.has(groutData.defaultGroutId)
    ? groutData.defaultGroutId
    : GROUT_COLORS[0]?.id;

export const useMosaicStore = defineStore('customMosaics', () => {
    // --- Static data ---
    const patterns = PATTERNS;
    const groutColors = GROUT_COLORS;

    // --- Design state ---
    const selectedPatternId = ref(null);
    /** @type {import('vue').Ref<Object<string, {name: string, hex: string}>>} */
    const colors = ref({});
    const groutId = ref(DEFAULT_GROUT_ID);

    // --- Painting tools ---
    const activeColorName = ref(null);
    /** 'tile' paints the clicked tile; 'fill' paints every tile sharing its color */
    const paintMode = ref('tile');
    const repeat = ref(MIN_REPEAT);

    // --- UI ---
    /** Open sidebar step: 1 = Pattern, 2 = Colors, 3 = Grout, 0 = none */
    const openStep = ref(1);

    // --- Glaze palette (Original Ceramic) ---
    const glazeColors = reactive(
        Object.fromEntries(
            Object.entries(staticGlazeColors).filter(([name]) => isOfferedGlaze(name)),
        ),
    );
    const glazeColorImages = reactive({});
    const glazeColorRecords = ref([]);

    // --- History ---
    const history = ref([]);
    const future = ref([]);
    let strokeSnapshot = null;
    let strokeChanged = 0;

    // --- Derived ---
    const selectedPattern = computed(() => PATTERNS_BY_ID.get(selectedPatternId.value) || null);
    const groutColor = computed(
        () => GROUT_BY_ID.get(groutId.value) || GROUT_BY_ID.get(DEFAULT_GROUT_ID),
    );
    const activeColor = computed(() => {
        const name = activeColorName.value;
        return name && glazeColors[name] ? { name, hex: glazeColors[name] } : null;
    });
    /** Tile id → display hex (current palette hex wins over the stored one) */
    const tileFills = computed(() => {
        const out = {};
        for (const [id, c] of Object.entries(colors.value)) {
            out[id] = glazeColors[c.name] || c.hex;
        }
        return out;
    });
    const paintedCount = computed(() => Object.keys(colors.value).length);
    const partsList = computed(() => buildPartsList(selectedPattern.value, colors.value));
    const canUndo = computed(() => history.value.length > 0);
    const canRedo = computed(() => future.value.length > 0);

    const shareUrl = computed(() => {
        const pattern = selectedPattern.value;
        if (!pattern || typeof window === 'undefined') return '';
        const base = `${window.location.origin}${window.location.pathname}`;
        return buildShareUrl(base, encodeDesign(pattern, colors.value, groutId.value));
    });

    // --- History helpers ---
    function snapshot() {
        return { colors: colors.value, groutId: groutId.value };
    }

    function restore(snap) {
        colors.value = snap.colors;
        groutId.value = snap.groutId;
    }

    function pushHistory(snap) {
        history.value.push(snap);
        if (history.value.length > MAX_HISTORY) history.value.shift();
        future.value = [];
    }

    function clearHistory() {
        history.value = [];
        future.value = [];
        strokeSnapshot = null;
        strokeChanged = 0;
    }

    const undo = () => {
        const prev = history.value.pop();
        if (!prev) return;
        future.value.push(snapshot());
        restore(prev);
    };

    const redo = () => {
        const next = future.value.pop();
        if (!next) return;
        history.value.push(snapshot());
        restore(next);
    };

    // --- Pattern selection ---
    /**
     * Selects a pattern and restores its saved colorway (if any).
     * @param {string} id
     * @returns {boolean} Whether the pattern exists
     */
    const selectPattern = (id) => {
        const pattern = PATTERNS_BY_ID.get(id);
        if (!pattern) return false;
        if (selectedPatternId.value === id) return true;
        const saved = loadDesign(pattern);
        selectedPatternId.value = id;
        colors.value = saved?.colors || {};
        if (saved?.grout?.id && GROUT_BY_ID.has(saved.grout.id)) groutId.value = saved.grout.id;
        clearHistory();
        return true;
    };

    // --- Painting ---
    const setActiveColor = (name) => {
        activeColorName.value = name && glazeColors[name] ? name : null;
    };

    const setPaintMode = (mode) => {
        paintMode.value = mode === 'fill' ? 'fill' : 'tile';
    };

    /** Applies the active color to `ids`; returns how many tiles changed. */
    function applyColor(ids) {
        const color = activeColor.value;
        if (!color) return 0;
        let next = null;
        let changed = 0;
        for (const id of ids) {
            if (colors.value[id]?.name === color.name) continue;
            next ??= { ...colors.value };
            next[id] = { name: color.name, hex: color.hex };
            changed += 1;
        }
        if (next) colors.value = next;
        return changed;
    }

    function idsForPaint(tileId) {
        const pattern = selectedPattern.value;
        if (!pattern) return [];
        if (paintMode.value !== 'fill') return [tileId];
        const target = colors.value[tileId]?.name || null;
        return pattern.tiles
            .filter((t) => (colors.value[t.id]?.name || null) === target)
            .map((t) => t.id);
    }

    /** Starts a paint stroke; everything until endStroke() is one undo step. */
    const beginStroke = () => {
        strokeSnapshot = snapshot();
        strokeChanged = 0;
    };

    /**
     * Paints a tile (or its color group in fill mode). Outside a stroke the
     * change is recorded as its own undo step.
     * @param {string} tileId
     * @returns {number} Tiles changed
     */
    const paintTile = (tileId) => {
        const inStroke = strokeSnapshot !== null;
        const before = snapshot();
        const changed = applyColor(idsForPaint(tileId));
        if (inStroke) strokeChanged += changed;
        else if (changed) pushHistory(before);
        return changed;
    };

    /**
     * Ends the current stroke.
     * @returns {number} Tiles changed during the stroke
     */
    const endStroke = () => {
        if (strokeSnapshot && strokeChanged > 0) pushHistory(strokeSnapshot);
        const changed = strokeChanged;
        strokeSnapshot = null;
        strokeChanged = 0;
        return changed;
    };

    /**
     * Paints every tile of a nominal size with the active color.
     * @param {string} size - e.g. `4x4`
     * @returns {number} Tiles changed
     */
    const paintSize = (size) => {
        const pattern = selectedPattern.value;
        if (!pattern) return 0;
        const before = snapshot();
        const changed = applyColor(pattern.tiles.filter((t) => t.size === size).map((t) => t.id));
        if (changed) pushHistory(before);
        return changed;
    };

    /** Returns every tile to unpainted. */
    const resetColors = () => {
        if (paintedCount.value === 0) return;
        pushHistory(snapshot());
        colors.value = {};
    };

    const setGrout = (id) => {
        if (!GROUT_BY_ID.has(id) || id === groutId.value) return;
        pushHistory(snapshot());
        groutId.value = id;
    };

    const setOpenStep = (n) => {
        openStep.value = [0, 1, 2, 3].includes(n) ? n : 1;
    };

    const setRepeat = (n) => {
        const v = Math.round(Number(n));
        if (!Number.isFinite(v)) return;
        repeat.value = Math.min(MAX_REPEAT, Math.max(MIN_REPEAT, v));
    };

    // --- Initialization ---
    /**
     * Applies a shared design from query params.
     * @param {Object<string, string>} query
     * @returns {boolean} Whether a valid pattern was found
     */
    const applySharedParams = (query) => {
        const pattern = PATTERNS_BY_ID.get(query?.p);
        if (!pattern) return false;
        selectedPatternId.value = pattern.id;
        colors.value = decodeColors(pattern, query);
        if (query.g && GROUT_BY_ID.has(query.g)) groutId.value = query.g;
        clearHistory();
        return true;
    };

    /**
     * Picks the starting design: a shared link wins, then the last edited
     * pattern, then the first pattern.
     * @param {Object<string, string>} [query]
     */
    const initialize = (query = {}) => {
        if (applySharedParams(query)) return;
        if (selectPattern(loadLastPatternId())) return;
        if (patterns[0]) selectPattern(patterns[0].id);
    };

    // --- Persistence ---
    let saveTimer = null;
    watch([selectedPatternId, colors, groutId], () => {
        if (!selectedPatternId.value) return;
        clearTimeout(saveTimer);
        saveTimer = setTimeout(() => {
            saveDesign({
                patternId: selectedPatternId.value,
                grout: { id: groutColor.value?.id, hex: groutColor.value?.hex },
                colors: colors.value,
            });
        }, SAVE_DEBOUNCE_MS);
    });

    // --- Algolia (Original Ceramic glazes) ---
    let loadToken = 0;

    function normalizeHit(hit) {
        const raw = hit.hexCode ?? hit.hex ?? hit.colorHex ?? '';
        const trimmed = String(raw).trim();
        const hex = trimmed ? (trimmed.startsWith('#') ? trimmed : `#${trimmed}`) : '';
        const img = hit.image;
        let imageUrl = '';
        if (Array.isArray(img) && img.length > 0) imageUrl = String(img[0]).split(' ')[0];
        else if (typeof img === 'string') imageUrl = img.split(' ')[0];
        return {
            name: hit.title,
            hex,
            order: hit.order ?? null,
            sheen: hit.sheen ?? null,
            imageUrl,
        };
    }

    const loadGlazeColorsFromAlgolia = async (appId, apiKey, indexName, token) => {
        try {
            const client = algoliasearch(appId, apiKey);
            const res = await client.search({
                requests: [
                    {
                        indexName,
                        query: '',
                        hitsPerPage: 1000,
                        attributesToRetrieve: [
                            'title',
                            'hexCode',
                            'hex',
                            'colorHex',
                            'order',
                            'sheen',
                            'image',
                        ],
                    },
                ],
            });
            if (token !== loadToken) return;
            const records = (res.results?.[0]?.hits ?? [])
                .filter((hit) => hit?.title && isOfferedGlaze(hit.title))
                .map(normalizeHit);
            const palette = derivePaletteFromRecords(records);
            log(`[loadGlazeColors] ${Object.keys(palette).length} glazes`);
            // Guard against a broken index wiping out the fallback palette
            if (Object.keys(palette).length < 8) return;
            for (const k of Object.keys(glazeColors)) delete glazeColors[k];
            Object.assign(glazeColors, palette);
            for (const k of Object.keys(glazeColorImages)) delete glazeColorImages[k];
            for (const r of records) if (r.imageUrl) glazeColorImages[r.name] = r.imageUrl;
            glazeColorRecords.value = records;
            if (activeColorName.value && !glazeColors[activeColorName.value]) {
                activeColorName.value = null;
            }
        } catch (err) {
            logError('[loadGlazeColors] ERROR:', err);
        }
    };

    /**
     * Loads Original Ceramic glazes from Algolia; keeps the static palette on failure.
     * @param {string} appId
     * @param {string} apiKey
     * @param {string} indexName
     */
    const initAlgolia = (appId, apiKey, indexName) => {
        if (!appId || !apiKey || !indexName) return;
        loadToken += 1;
        loadGlazeColorsFromAlgolia(appId, apiKey, indexName, loadToken);
    };

    const cleanup = () => {
        clearTimeout(saveTimer);
    };

    return {
        // Static data
        patterns,
        groutColors,
        patternErrors: PATTERN_ERRORS,

        // State
        selectedPatternId,
        colors,
        groutId,
        activeColorName,
        paintMode,
        repeat,
        openStep,
        glazeColors,
        glazeColorImages,
        glazeColorRecords,

        // Derived
        selectedPattern,
        groutColor,
        activeColor,
        tileFills,
        paintedCount,
        partsList,
        shareUrl,
        canUndo,
        canRedo,

        // Actions
        initialize,
        applySharedParams,
        selectPattern,
        setActiveColor,
        setPaintMode,
        beginStroke,
        paintTile,
        endStroke,
        paintSize,
        resetColors,
        setGrout,
        setRepeat,
        setOpenStep,
        undo,
        redo,
        initAlgolia,
        cleanup,
    };
});
